import { DestroyRef, Injectable, Signal, effect, inject, signal } from '@angular/core';
import { environment } from '../../../environments/environment';
import { DebugLayoutToggleService } from './debug-layout-toggle.service';

/**
 * Non-production helper that flips a global "error compression" signal when
 * the user presses Ctrl+Alt+E anywhere in the app.
 *
 * Why: validation errors on Statement of Changes in Equity (role19) rows can
 * be very long — a single cell may stack several ERROR lines plus a few
 * "* SUGGESTED CHANGE" warnings, which pushes the table layout around and
 * makes it hard to scan. Developers reviewing a sheet often only want a
 * quick "how many problems does this row have" count rather than the full
 * prose. When this toggle is ON, the SoCIE error-description cells render a
 * compressed "X errors, Y warnings" summary instead of the full HTML.
 *
 * Scope: only the SoCIE sheet consumes this signal. The toggle is OFF by
 * default, so with no keypress the sheet renders exactly as it does today.
 *
 * Unlike `DebugLayoutToggleService` (Ctrl+Alt+D) there is NO layout swap
 * here — nothing is destroyed/recreated, only a per-cell string vs. HTML
 * branch — so there is no `transitioning` / `effectiveEnabled` / double-rAF
 * machinery. A single `enabled` signal that flips synchronously on the
 * keypress is sufficient.
 *
 * Scope guard: the keypress is ignored while the Ctrl+Alt+D debug layout is
 * active (`DebugLayoutToggleService.effectiveEnabled()`). The compression
 * toggle only affects cells of the ORIGINAL 4-table grid, which is hidden
 * under the debug layout; suppressing it there also frees `Ctrl+Alt+E` for
 * the debug layout's own "jump to next failing row" binding. Net effect:
 * this toggle is operational only on the original grid.
 *
 * The listener is never attached in production builds — the
 * `environment.production` check is a compile-time constant so the body is
 * dead-code eliminated by the Angular optimiser. Consumers additionally
 * guard the template branch with `EnvironmentService.isProduction()`.
 */
@Injectable({ providedIn: 'root' })
export class DebugErrorCompressionToggleService {
  private readonly _enabled = signal<boolean>(false);

  /** Toggle state — what templates should render against. */
  readonly enabled: Signal<boolean> = this._enabled.asReadonly();

  private readonly debugLayout = inject(DebugLayoutToggleService);
  private readonly handler = (event: KeyboardEvent) => this.onKey(event);
  private refCount = 0;

  constructor() {
    // Entering the Ctrl+Alt+D debug layout forces this toggle OFF, so when the
    // user returns to the original 4-table grid it renders in its default
    // (full error HTML) state rather than silently still-compressed. Reads the
    // intended `enabled` signal (flips synchronously on the keypress), so the
    // reset lands the moment the user switches into debug mode.
    effect(() => {
      if (this.debugLayout.enabled()) this._enabled.set(false);
    });
  }

  bindGlobalShortcut(destroyRef: DestroyRef): void {
    if (environment.production) return;

    if (this.refCount === 0) {
      window.addEventListener('keydown', this.handler);
    }
    this.refCount++;

    destroyRef.onDestroy(() => {
      this.refCount = Math.max(0, this.refCount - 1);
      if (this.refCount === 0) {
        window.removeEventListener('keydown', this.handler);
      }
    });
  }

  private onKey(event: KeyboardEvent): void {
    if (!event.ctrlKey || !event.altKey || event.shiftKey || event.metaKey) return;
    if (event.code !== 'KeyE') return;
    // Operate only on the original 4-table grid — never under Ctrl+Alt+D
    // (where the original grid is hidden and Ctrl+Alt+E means "jump to next
    // failing row" inside the debug layout).
    if (this.debugLayout.effectiveEnabled()) return;
    event.preventDefault();

    this._enabled.update((isEnabled) => !isEnabled);
  }
}
